package com.arena_web.ui.components.cards

import androidx.compose.runtime.*
import com.arena_web.data.dto.EventParticipantDto
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

/**
 * Player card sizes matching Figma design system.
 */
enum class PlayerCardSize(val w: Int, val h: Int) {
    Large(254, 385),
    Medium(130, 202),
    Small(130, 204),
    Arena(94, 147),
    XtraSmall(58, 90),
    Highlight(37, 58)
}

data class PlayerStat(val value: Int, val label: String)

private const val CDN_BASE = "https://images.arenaproplay.com/"

/**
 * Warms the browser image cache (fetch + decode) for a roster's card art —
 * avatar, card frame and rating plate — so off-screen pages in a paged grid
 * paint instantly on the first flip instead of decoding on slide-in.
 */
fun preloadPlayerCardAssets(participants: List<EventParticipantDto>) {
    val urls = LinkedHashSet<String>()
    participants.forEach { p ->
        p.cardAvatarUrl?.takeIf { it.isNotBlank() }?.let { urls.add(it) }
        p.cardAssetsBasePath?.takeIf { it.isNotBlank() }?.let { base ->
            urls.add("$CDN_BASE$base/card.png")
            urls.add("$CDN_BASE$base/card_rating.png")
        }
    }
    urls.forEach { url ->
        val img = kotlinx.browser.document.createElement("img").unsafeCast<org.w3c.dom.HTMLImageElement>()
        img.src = url
        // decode() pre-rasterises into the image cache; ignore failures.
        runCatching { img.asDynamic().decode() }
    }
}

// Soft white outline around the player name — gives the blue glyph some
// lift over the card art without the corner stops merging into a solid
// block on tighter event-card layouts. Stroke opacity halved and halo
// blur tightened so the ring stays per-letter even at small sizes.
private fun Modifier.cardTextOutline(): Modifier = styleModifier {
    property(
        "text-shadow",
        "-1px -1px 0 rgba(0, 97, 255, 0.05), " +
            "1px -1px 0 rgba(0, 97, 255, 0.05), " +
            "-1px 1px 0 rgba(0, 97, 255, 0.05), " +
            "1px 1px 0 rgba(0, 97, 255, 0.05), " +
            "0 0 2px rgba(0, 97, 255, 0.05)"
    )
}

@Composable
fun PlayerCard(
    playerName: String = "",
    rating: Int = 0,
    position: String = "",
    sport: String = "football",
    size: PlayerCardSize = PlayerCardSize.Large,
    stats: List<PlayerStat> = emptyList(),
    showPlate: Boolean = false,
    plateLabel: String = "",
    // When true the plate (`Res`, etc.) is rendered on the RIGHT side of
    // the card instead of the default LEFT. Used for right-team bench
    // cards so the plate always faces inward to the field.
    plateOnRight: Boolean = false,
    cardFrameUrl: String = "",
    avatarUrl: String? = null,
    flagUrl: String? = null,
    teamEmblemUrl: String? = null,
    cardAssetsBasePath: String? = null,
    cardTextColor: String? = null,
    hideRating: Boolean = false,
    hideShadow: Boolean = false,
    // When true, the outer Box fills its parent container and the card
    // contents (rendered at the chosen `size`'s design pixel grid) are
    // scaled via `transform: scale(min(100cqw/size.w, 100cqh/size.h))` so
    // the visual card grows or shrinks with the surrounding slot. Default
    // false keeps every existing caller pixel-locked to `size.w × size.h`.
    fillContainer: Boolean = false,
    // When the surrounding `--scale` isn't 1 (desktop), the fit ratio must
    // account for it so the card fills the slot. Mobile keeps this OFF — there
    // `--scale` is 1 and the original px math already fits exactly.
    fillContainerScaleAware: Boolean = false,
    modifier: Modifier = Modifier
) {
    val sc = SportColors.forSport(sport)

    val isOther = sport.lowercase() == "other"

    val effectiveRating = if (hideRating) 0 else rating
    val effectivePosition = if (hideRating) "" else position
    val effectiveFlag = if (hideRating) null else flagUrl
    val effectiveEmblem = if (hideRating) null else teamEmblemUrl

    val card = @Composable {
        ImageCardFrame(
            size,
            sc,
            playerName,
            effectiveRating,
            effectivePosition,
            stats,
            cardFrameUrl,
            avatarUrl,
            effectiveFlag,
            effectiveEmblem,
            cardAssetsBasePath,
            cardTextColor,
            isOther,
            hideRating,
            hideShadow,
            // Grid cards (fillContainer) preload overlays so flipping pages shows
            // name/rating/avatar with no reveal delay.
            eagerOverlays = fillContainer,
        )
    }

    if (showPlate) {
        Row(modifier, verticalAlignment = Alignment.Bottom) {
            if (plateOnRight) {
                Box(Modifier.styleModifier { property("z-index", "1") }) { card() }
                Plate(
                    plateLabel,
                    if (size == PlayerCardSize.Large) 369 else 210,
                    mirrored = true,
                )
            } else {
                Plate(plateLabel, if (size == PlayerCardSize.Large) 369 else 210)
                Box(Modifier.styleModifier { property("z-index", "1") }) { card() }
            }
        }
    } else if (fillContainer) {
        // Outer box is the size-container — its width/height come from the
        // caller's slot, and cqw/cqh below resolve against it. The inner
        // wrapper preserves the design grid (`size.w × size.h` in px) and
        // applies a uniform scale so the card always fits without
        // distorting its internal layout.
        Box(
            modifier = modifier
                .fillMaxSize()
                .styleModifier {
                    property("container-type", "size")
                    property("position", "relative")
                    property("overflow", "visible")
                }
        ) {
            Box(
                modifier = Modifier
                    .sWidth(size.w)
                    .sHeight(size.h)
                    .styleModifier {
                        property("position", "absolute")
                        property("left", "50%")
                        property("top", "0")
                        property(
                            "transform",
                            run {
                                // Inner box is sWidth(size.w) = size.w × --scale.
                                // `--scale` is itself a LENGTH (e.g. 0.65px), so the
                                // scale-aware divisor is `size.w × var(--scale)` (a
                                // length) — dividing 100cqw by it gives the ratio
                                // that makes the card fill the slot at any --scale.
                                // Mobile keeps the plain px ratio (--scale is 1px).
                                val wDiv = if (fillContainerScaleAware) "(${size.w} * var(--scale))" else "${size.w}px"
                                val hDiv = if (fillContainerScaleAware) "(${size.h} * var(--scale))" else "${size.h}px"
                                "translateX(-50%) scale(min(calc(100cqw / $wDiv), calc(100cqh / $hDiv)))"
                            },
                        )
                        property("transform-origin", "top center")
                    }
            ) { card() }
        }
    } else {
        Box(modifier) { card() }
    }
}

/**
 * Simplified player card for chat — shows avatar + name only, no rating/position.
 */
@Composable
fun ChatPlayerCard(
    playerName: String = "",
    sport: String = "football",
    size: PlayerCardSize = PlayerCardSize.XtraSmall,
    avatarUrl: String? = null,
    cardAssetsBasePath: String? = null,
    cardTextColor: String? = null,
    modifier: Modifier = Modifier
) {
    val sc = SportColors.forSport(sport)
    val effectiveSc = if (!cardTextColor.isNullOrBlank()) {
        val hex = cardTextColor.removePrefix("#")
        sc.copy(primary = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(
            hex.substring(0, 2).toInt(16),
            hex.substring(2, 4).toInt(16),
            hex.substring(4, 6).toInt(16)
        ))
    } else sc

    val hasAssets = !cardAssetsBasePath.isNullOrBlank()
    val bgUrl = if (hasAssets) "${CDN_BASE}${cardAssetsBasePath}/card.png" else ""
    val resolvedAvatar = avatarUrl ?: AppIcons.EmptyAvatar

    Box(
        modifier
            .sWidth(size.w)
            .sHeight(size.h)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("position", "relative")
                if (bgUrl.isNotBlank()) {
                    property("background", "url('$bgUrl') center/cover no-repeat")
                    property("background-size", "100% 100%")
                }
            }
    ) {
        AvatarOverlay(size, resolvedAvatar, maskUrl = bgUrl.takeIf { it.isNotBlank() })
        if (playerName.isNotBlank()) {
            PlayerInfoBlock(size, effectiveSc, playerName, emptyList(), flagUrl = null)
        }
    }
}

// =============================================================================
// Plate (left tab for M/Coach variants)
// =============================================================================

@Composable
private fun Plate(label: String, height: Int, mirrored: Boolean = false) {
    Box(
        Modifier
            .sWidth(73)
            .sHeight(height)
            .styleModifier {
                property("border-radius", "${s(15)} ${s(5)} ${s(5)} ${s(5)}")
                property(
                    "background",
                    "linear-gradient(228deg, rgba(56,124,255,0.27) 70%, rgba(5,4,12,0.03) 100%)"
                )
                if (mirrored) {
                    // Right-side plate sits AFTER the card in the row and
                    // slides under its left edge via `margin-left`. A
                    // horizontal flip (`scaleX(-1)`) mirrors the rounded
                    // corner + label so the tab faces the card from the
                    // right while keeping the label upright.
                    property("margin-left", s(-45))
                    property("transform", "scaleX(-1)")
                } else {
                    property("margin-right", s(-45))
                }
                property("position", "relative")
            }
    ) {
        SpanText(
            label,
            modifier = Modifier
                .bodyLMedium()
                .color(AppColors.Text.Primary)
                .styleModifier {
                    property("position", "absolute")
                    // Mirrored plate: label moves to the BOTTOM of the tab
                    // (the rounded corner is now at the bottom thanks to the
                    // parent `scaleX(-1)`). Also counter-flips the glyphs so
                    // `Res` reads forward.
                    if (mirrored) {
                        property("right", s(14))
                        property("bottom", s(30))
                        property("transform", "translateX(50%) rotate(-90deg) scaleX(-1)")
                    } else {
                        property("left", s(14))
                        property("top", s(30))
                        property("transform", "translateX(-50%) rotate(-90deg)")
                    }
                    property("white-space", "nowrap")
                }
        )
    }
}

// =============================================================================
// Image-based card frame (full card image from Figma)
// =============================================================================

@Composable
private fun ImageCardFrame(
    size: PlayerCardSize,
    sc: SportColorScheme,
    playerName: String,
    rating: Int,
    position: String,
    stats: List<PlayerStat>,
    cardFrameUrl: String,
    avatarUrl: String?,
    flagUrl: String?,
    teamEmblemUrl: String?,
    cardAssetsBasePath: String?,
    cardTextColor: String?,
    isOther: Boolean = false,
    hideRating: Boolean = false,
    hideShadow: Boolean = false,
    // Skip the 100ms reveal delay so the name/rating/avatar are present from the
    // first frame (used by the players grid so off-screen pages are preloaded).
    eagerOverlays: Boolean = false,
) {
    var showOverlays by remember { mutableStateOf(eagerOverlays) }
    if (!eagerOverlays) {
        LaunchedEffect(Unit) {
            kotlinx.coroutines.delay(100)
            showOverlays = true
        }
    }

    val hasAssets = !cardAssetsBasePath.isNullOrBlank()
    val bgUrl = if (hasAssets) "${CDN_BASE}${cardAssetsBasePath}/card.png" else cardFrameUrl

    // Per-kit text color from backend, or fall back to sport color scheme
    val effectiveSc = if (!cardTextColor.isNullOrBlank()) {
        val hex = cardTextColor.removePrefix("#")
        sc.copy(primary = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(
            hex.substring(0, 2).toInt(16),
            hex.substring(2, 4).toInt(16),
            hex.substring(4, 6).toInt(16)
        ))
    } else sc

    Box(
        Modifier
            .sWidth(size.w)
            .sHeight(size.h)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("position", "relative")
                property("background", "url('${bgUrl}') center/cover no-repeat")
                property("background-size", "100% 100%")
            }
    ) {
        if (showOverlays) {
            if (avatarUrl != null) {
                AvatarOverlay(size, avatarUrl, maskUrl = bgUrl.takeIf { it.isNotBlank() })
                if (hasAssets && size == PlayerCardSize.Arena) {
                    // Arena layout: name centered + rating bottom center with emblems on sides
                    if (playerName.isNotBlank()) {
                        PlayerInfoBlock(size, effectiveSc, playerName, stats, flagUrl = null, isOther = isOther)
                    }
                    if (!isOther && !hideRating) {
                        ArenaBottomRating(
                            cardAssetsBasePath = cardAssetsBasePath!!,
                            sc = effectiveSc,
                            rating = rating,
                            position = position,
                            flagUrl = flagUrl,
                            teamEmblemUrl = teamEmblemUrl
                        )
                    }
                } else if (hasAssets) {
                    // Figma: card_rating container with rating info inside
                    if (!isOther && !hideRating) {
                        AssetRatingContainer(
                            cardAssetsBasePath = cardAssetsBasePath!!,
                            sc = effectiveSc,
                            rating = rating,
                            position = position,
                            flagUrl = flagUrl,
                            teamEmblemUrl = teamEmblemUrl
                        )
                    }
                    // Name + stats (no IdentityColumn — it's inside card_rating)
                    if (playerName.isNotBlank()) {
                        PlayerInfoBlock(size, effectiveSc, playerName, stats, flagUrl = null, isOther = isOther)
                    }
                } else {
                    CardOverlays(size, sc, playerName, rating, position, stats, flagUrl, teamEmblemUrl, isOther)
                }
            }
        }
    }
}

@Composable
private fun CardAssetLayer(
    src: String,
    left: String,
    top: String,
    width: String,
    height: String
) {
    Image(
        src = src,
        modifier = Modifier
            .styleModifier {
                property("position", "absolute")
                property("left", left)
                property("top", top)
                property("width", width)
                property("height", height)
                property("pointer-events", "none")
            }
    )
}

// =============================================================================
// Public, position-agnostic rating badge — `card_rating.png` background with
// rating + divider + position centered. Used by the front (AssetRatingContainer
// wraps it with absolute positioning) and by the back-side radar layout, which
// supplies its own absolute positioning. Doesn't render emblems.
// =============================================================================
@Composable
fun PlayerCardRatingBadge(
    cardAssetsBasePath: String,
    rating: Int,
    position: String,
    modifier: Modifier = Modifier,
    cardTextColor: String? = null,
) {
    val sportColors = com.arena_web.ui.theme.LocalSportColors.current
    // Per-card override (e.g. "#1E7FF5" from the card asset metadata) wins over
    // the sport default so the back-side rating matches the front's name colour.
    val accent = if (!cardTextColor.isNullOrBlank()) {
        val hex = cardTextColor.removePrefix("#")
        runCatching {
            val r = hex.substring(0, 2).toInt(16)
            val g = hex.substring(2, 4).toInt(16)
            val b = hex.substring(4, 6).toInt(16)
            com.varabyte.kobweb.compose.ui.graphics.Color.rgb(r, g, b)
        }.getOrDefault(sportColors.primary)
    } else sportColors.primary
    val rgb = accent.toRgb()
    val hasPosition = position.isNotBlank()

    Box(
        modifier = modifier
            .styleModifier { property("overflow", "visible") },
        contentAlignment = Alignment.Center
    ) {
        Image(
            src = "${CDN_BASE}${cardAssetsBasePath}/card_rating.png",
            modifier = Modifier.fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("pointer-events", "none")
                }
        )
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.styleModifier {
                property("z-index", "1")
            }
        ) {
            SpanText(
                "$rating",
                modifier = Modifier
                    .fontFamily(
                        AppTypography.CARD_HEADLINE_FONT,
                        *AppTypography.CARD_HEADLINE_FALLBACKS
                    )
                    .fontWeight(AppTypography.SemiBold)
                    .sFontSize(22)
                    .sLineHeight(26)
                    .color(accent)
                    .textAlign(TextAlign.Center)
                    .styleModifier {
                        property("text-transform", "uppercase")
                        property("letter-spacing", s(0.22))
                    }
            )
            if (hasPosition) {
                Box(
                    Modifier
                        .sWidth(24)
                        .styleModifier {
                            property("height", "0.5px")
                            property(
                                "background",
                                "linear-gradient(90deg, rgba(${rgb.red},${rgb.green},${rgb.blue},0), rgba(${rgb.red},${rgb.green},${rgb.blue},0.6), rgba(${rgb.red},${rgb.green},${rgb.blue},0))"
                            )
                        }
                )
                SpanText(
                    position,
                    modifier = Modifier
                        .fontFamily(AppTypography.CARD_TEXT_FONT, AppTypography.CARD_TEXT_FALLBACK)
                        .fontWeight(AppTypography.SemiBold)
                        .sFontSize(16)
                        .sLineHeight(16)
                        .color(accent)
                        .textAlign(TextAlign.Center)
                        .styleModifier {
                            property("text-transform", "uppercase")
                            property("letter-spacing", s(-0.16))
                        }
                )
            }
        }
    }
}

// =============================================================================
// Card rating container — card_rating.png with rating/position/emblems inside
// Figma: Container_Rating at (18, 58) 66×100 on 238×369 card
// =============================================================================

@Composable
private fun AssetRatingContainer(
    cardAssetsBasePath: String,
    sc: SportColorScheme,
    rating: Int,
    position: String,
    flagUrl: String?,
    teamEmblemUrl: String?
) {
    val hasPosition = position.isNotBlank()
    val hasEmblems = flagUrl != null || teamEmblemUrl != null
    val rgb = sc.primary.toRgb()

    // Scale container proportionally based on available data
    // Full (66×100), no emblems (53×80, ×0.8), rating only (36×55, ×0.55)
    val scale = when {
        hasPosition && hasEmblems -> 1.0
        hasPosition -> 0.8
        else -> 0.55
    }
    val containerWidth = "${27.73 * scale}%"   // base 66/238 = 27.73%
    val containerHeight = "${27.1 * scale}%"   // base 100/369 = 27.1%

    Box(
        modifier = Modifier.styleModifier {
            property("position", "absolute")
            property("left", "9.56%")   // 18/238
            property("top", "16.72%")   // 58/369
            property("width", containerWidth)
            property("height", containerHeight)
            property("overflow", "visible")
        },
        contentAlignment = Alignment.Center
    ) {
        // card_rating.png — Figma: slight overflow (105.96% × 109.59%, offset -2.83%, -6.92%)
        Image(
            src = "${CDN_BASE}${cardAssetsBasePath}/card_rating.png",
            modifier = Modifier.fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("pointer-events", "none")
                }
        )

        // Rating info — centered in container; auto-centers when data is missing
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.styleModifier {
                property("gap", s(5))
                property("z-index", "1")
            }
        ) {
            // Rating Value group — rating + divider + position (no gap between them)
            Column(
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    "$rating",
                    modifier = Modifier
                        .fontFamily(
                            AppTypography.CARD_HEADLINE_FONT,
                            *AppTypography.CARD_HEADLINE_FALLBACKS
                        )
                        .fontWeight(AppTypography.SemiBold)
                        .sFontSize(22)
                        .sLineHeight(26)
                        .color(sc.primary)
                        .textAlign(TextAlign.Center)
                        .styleModifier {
                            property("text-transform", "uppercase")
                            property("letter-spacing", s(0.22))
                        }
                )

                if (hasPosition) {
                    // Divider — Figma: 24px gradient line
                    Box(
                        Modifier
                            .sWidth(24)
                            .styleModifier {
                                property("height", "0.5px")
                                property(
                                    "background",
                                    "linear-gradient(90deg, rgba(${rgb.red},${rgb.green},${rgb.blue},0), rgba(${rgb.red},${rgb.green},${rgb.blue},0.6), rgba(${rgb.red},${rgb.green},${rgb.blue},0))"
                                )
                            }
                    )
                    SpanText(
                        position,
                        modifier = Modifier
                            .fontFamily(AppTypography.CARD_TEXT_FONT, AppTypography.CARD_TEXT_FALLBACK)
                            .fontWeight(AppTypography.SemiBold)
                            .sFontSize(16)
                            .sLineHeight(16)
                            .color(sc.primary)
                            .textAlign(TextAlign.Center)
                            .styleModifier {
                                property("text-transform", "uppercase")
                                property("letter-spacing", s(-0.16))
                            }
                    )
                }
            }

            // Emblems row — Figma: flag 18×12, logo 19×15, gap 4
            if (hasEmblems) {
                Row(
                    modifier = Modifier.styleModifier { property("gap", s(4)) },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (flagUrl != null) {
                        Image(
                            src = flagUrl,
                            modifier = Modifier.sWidth(18).sHeight(12)
                                .styleModifier { property("object-fit", "cover") },
                            alt = "flag"
                        )
                    }
                    if (teamEmblemUrl != null) {
                        Image(
                            src = teamEmblemUrl,
                            modifier = Modifier.sWidth(19).sHeight(15)
                                .styleModifier { property("object-fit", "contain") },
                            alt = "emblem"
                        )
                    }
                }
            }
        }
    }
}

/**
 * Arena-specific bottom-center rating layout:
 * [club emblem] [card_rating with rating/position] [country flag]
 */
@Composable
private fun ArenaBottomRating(
    cardAssetsBasePath: String,
    sc: SportColorScheme,
    rating: Int,
    position: String,
    flagUrl: String?,
    teamEmblemUrl: String?
) {
    val rgb = sc.primary.toRgb()
    val hasPosition = position.isNotBlank()

    Row(
        modifier = Modifier.styleModifier {
            property("position", "absolute")
            property("top", "69%")
            property("left", "50%")
            property("transform", "translateX(-50%)")
            property("gap", s(3))
            property("align-items", "center")
            property("z-index", "2")
            // Own compositing layer (like the avatar) so the rating is
            // rasterised up-front and isn't repainted on slide-in.
            property("will-change", "transform")
        },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Club emblem — left side
        if (teamEmblemUrl != null) {
            Image(
                src = teamEmblemUrl,
                modifier = Modifier.sWidth(17).sHeight(13)
                    .styleModifier { property("object-fit", "contain") },
                alt = "emblem"
            )
        }

        // card_rating container with rating + position
        Box(
            modifier = Modifier
                .sWidth(25).sHeight(38)
                .styleModifier {
                    property("position", "relative")
                    property("flex-shrink", "0")
                },
            contentAlignment = Alignment.Center
        ) {
            Image(
                src = "${CDN_BASE}${cardAssetsBasePath}/card_rating.png",
                modifier = Modifier.fillMaxSize()
                    .styleModifier {
                        property("position", "absolute")
                        property("pointer-events", "none")
                    }
            )
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.styleModifier { property("z-index", "1") }
            ) {
                SpanText(
                    "$rating",
                    modifier = Modifier
                        .fontFamily(AppTypography.CARD_HEADLINE_FONT, *AppTypography.CARD_HEADLINE_FALLBACKS)
                        .fontWeight(AppTypography.SemiBold)
                        .sFontSize(10)
                        .sLineHeight(12)
                        .color(sc.primary)
                        .textAlign(TextAlign.Center)
                        .styleModifier {
                            property("text-transform", "uppercase")
                            property("letter-spacing", s(0.1))
                        }
                )
                if (hasPosition) {
                    Box(
                        Modifier.sWidth(13).styleModifier {
                            property("height", "0.5px")
                            property("background", "linear-gradient(90deg, rgba(${rgb.red},${rgb.green},${rgb.blue},0), rgba(${rgb.red},${rgb.green},${rgb.blue},0.6), rgba(${rgb.red},${rgb.green},${rgb.blue},0))")
                        }
                    )
                    SpanText(
                        position,
                        modifier = Modifier
                            .fontFamily(AppTypography.CARD_TEXT_FONT, AppTypography.CARD_TEXT_FALLBACK)
                            .fontWeight(AppTypography.SemiBold)
                            .sFontSize(6)
                            .sLineHeight(6)
                            .color(sc.primary)
                            .textAlign(TextAlign.Center)
                            .styleModifier {
                                property("text-transform", "uppercase")
                                property("letter-spacing", s(-0.06))
                            }
                    )
                }
            }
        }

        // Country flag — right side
        if (flagUrl != null) {
            Image(
                src = flagUrl,
                modifier = Modifier.sWidth(14).sHeight(9)
                    .styleModifier { property("object-fit", "cover") },
                alt = "flag"
            )
        }
    }
}

@Composable
private fun AvatarOverlay(size: PlayerCardSize, avatarUrl: String, maskUrl: String? = null) {
    val isDefault = avatarUrl.contains("ic_empty_avatar")

    // Figma reference: figure fills the whole card, anchored at top.
    // The card.png alpha is applied as a CSS mask so the avatar follows the card's
    // irregular silhouette (notches/cutouts) instead of being a plain rectangle.
    Image(
        src = avatarUrl,
        modifier = Modifier
            .styleModifier {
                property("position", "absolute")
                property("top", "0")
                property("left", "0")
                property("width", "100%")
                property("height", "100%")
                property("object-fit", "cover")
                property("object-position", "center 55%")
                property("transform", "scale(0.87)")
                property("transform-origin", "center 55%")
                property("pointer-events", "none")
                property("will-change", "transform")
                property("backface-visibility", "hidden")
                if (!isDefault) property("filter", "contrast(1.1) brightness(1.02)")
                if (maskUrl != null) {
                    property("-webkit-mask-image", "url('$maskUrl')")
                    property("-webkit-mask-size", "100% 100%")
                    property("-webkit-mask-repeat", "no-repeat")
                    property("-webkit-mask-position", "center")
                    property("mask-image", "url('$maskUrl')")
                    property("mask-size", "100% 100%")
                    property("mask-repeat", "no-repeat")
                    property("mask-position", "center")
                }
            }
    )
}

// =============================================================================
// Card overlays — layout differs per size
// =============================================================================

@Composable
private fun CardOverlays(
    size: PlayerCardSize,
    sc: SportColorScheme,
    playerName: String,
    rating: Int,
    position: String,
    stats: List<PlayerStat>,
    flagUrl: String?,
    teamEmblemUrl: String?,
    isOther: Boolean = false
) {
    if (size == PlayerCardSize.Highlight) return

    // Identity column (left side: rating, position, flag, emblem)
    if (!isOther && (rating > 0 || position.isNotBlank())) {
        IdentityColumn(
            size, sc, rating, position,
            flagUrl = null,
            teamEmblemUrl = teamEmblemUrl
        )
    }

    // Player info block (center-bottom: name, divider, stats)
    if (playerName.isNotBlank()) {
        PlayerInfoBlock(
            size, sc, playerName, stats,
            flagUrl = flagUrl,
            isOther = isOther
        )
    }
}

// =============================================================================
// Bottom darkening gradient overlay (Figma "Background" layer)
// =============================================================================

@Composable
private fun BottomDarkening(size: PlayerCardSize) {
    val left: Double
    val top: Double
    val w: Double
    val h: Double
    val overflowV: Double
    val overflowH: Double

    when (size) {
        PlayerCardSize.Large -> {
            left = 18.0; top = 164.0; w = 199.0; h = 154.5
            overflowV = 0.4798; overflowH = 0.3761
        }

        PlayerCardSize.Medium, PlayerCardSize.Small -> {
            left = 8.8; top = 95.75; w = 113.025; h = 84.972
            overflowV = 0.4798; overflowH = 0.3642
        }

        PlayerCardSize.Arena -> {
            left = 6.4; top = 69.0; w = 81.5; h = 61.3
            overflowV = 0.4798; overflowH = 0.3642
        }

        PlayerCardSize.XtraSmall -> {
            left = 3.9; top = 42.43; w = 50.122; h = 37.682
            overflowV = 0.4869; overflowH = 0.3695
        }

        else -> return
    }

    val actualW = w * (1 + 2 * overflowH)
    val actualH = h * (1 + 2 * overflowV)
    val actualLeft = left - w * overflowH
    val actualTop = top - h * overflowV

    Box(
        Modifier.styleModifier {
            property("position", "absolute")
            property("left", s(actualLeft))
            property("top", s(actualTop))
            property("width", s(actualW))
            property("height", s(actualH))
            property(
                "background",
                "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 30%, rgba(0,0,0,0.15) 55%, transparent 75%)"
            )
            property("pointer-events", "none")
        }
    )
}

// =============================================================================
// Identity column (left side: rating, position, dividers, flag, emblem)
// =============================================================================

@Composable
private fun IdentityColumn(
    size: PlayerCardSize,
    sc: SportColorScheme,
    rating: Int,
    position: String,
    flagUrl: String?,
    teamEmblemUrl: String?
) {
    val left: String
    val top: String
    val colHeight: String
    val colPadding: String
    val ratingFS: Int
    val ratingLH: Int
    val posFS: Int
    val posLH: Int
    val valueGap: String
    val sectionGap: String
    val emblemW: Int

    when (size) {
        PlayerCardSize.Large -> {
            left = s(14); top = s(48); colHeight = s(279)
            colPadding = "${s(32)} ${s(12)} ${s(103)}"
            ratingFS = 22; ratingLH = 26; posFS = 16; posLH = 16
            valueGap = s(2); sectionGap = s(8); emblemW = 30
        }

        PlayerCardSize.Medium, PlayerCardSize.Small -> {
            left = s(9); top = s(25); colHeight = s(153)
            colPadding = "0 ${s(6)} ${s(72)}"
            ratingFS = 12; ratingLH = 12; posFS = 10; posLH = 12
            valueGap = s(1.1); sectionGap = s(4.4); emblemW = 16
        }

        PlayerCardSize.Arena -> {
            left = s(6); top = s(18); colHeight = s(110)
            colPadding = "0 ${s(6)} ${s(34)}"
            ratingFS = 12; ratingLH = 12; posFS = 10; posLH = 12
            valueGap = s(1.1); sectionGap = s(4.4); emblemW = 16
        }

        PlayerCardSize.XtraSmall -> {
            left = s(4); top = s(12); colHeight = s(68)
            colPadding = "0 ${s(4)} ${s(32)}"
            ratingFS = 8; ratingLH = 6; posFS = 8; posLH = 5
            valueGap = s(1); sectionGap = s(2); emblemW = 7
        }

        else -> return
    }

    Box(
        Modifier.styleModifier {
            property("position", "absolute")
            property("left", left)
            property("top", top)
            property("height", colHeight)
            property("padding", colPadding)

            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
        }
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.styleModifier { property("gap", sectionGap) }
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.styleModifier { property("gap", valueGap) }
            ) {
                SpanText(
                    "$rating",
                    modifier = Modifier
                        .fontFamily(
                            AppTypography.CARD_HEADLINE_FONT,
                            *AppTypography.CARD_HEADLINE_FALLBACKS
                        )
                        .fontWeight(AppTypography.Bold)
                        .sFontSize(ratingFS)
                        .sLineHeight(ratingLH)
                        .color(sc.primary)
                        .textAlign(TextAlign.Center)
                        .styleModifier { property("text-transform", "uppercase") }
                )
                SportDivider(sc)
                SpanText(
                    position,
                    modifier = Modifier
                        .fontFamily(AppTypography.CARD_TEXT_FONT, AppTypography.CARD_TEXT_FALLBACK)
                        .fontWeight(AppTypography.SemiBold)
                        .sFontSize(posFS)
                        .sLineHeight(posLH)
                        .color(sc.primary)
                        .textAlign(TextAlign.Center)
                        .styleModifier { property("text-transform", "uppercase") }
                )
                SportDivider(sc)
            }

            if (flagUrl != null) {
                Image(
                    src = flagUrl,
                    modifier = Modifier.sWidth(emblemW)
                        .styleModifier {
                            property("aspect-ratio", "275/183"); property(
                            "object-fit",
                            "cover"
                        )
                        },
                    alt = "flag"
                )
            }

            if (teamEmblemUrl != null) {
                Image(
                    src = teamEmblemUrl,
                    modifier = Modifier.sWidth(emblemW)
                        .styleModifier {
                            property("aspect-ratio", "142/144"); property(
                            "object-fit",
                            "cover"
                        )
                        },
                    alt = "emblem"
                )
            }
        }
    }
}

// =============================================================================
// Player info block (name + flag/stats area, center-bottom)
// =============================================================================

@Composable
private fun PlayerInfoBlock(
    size: PlayerCardSize,
    sc: SportColorScheme,
    playerName: String,
    stats: List<PlayerStat>,
    flagUrl: String?,
    isOther: Boolean = false
) {
    val left: String
    val top: String
    val blockWidth: String
    val nameFS: Int
    val nameLH: Int
    val gap: String
    val flagW: Int
    val flagH: Int

    when (size) {
        PlayerCardSize.Large -> {
            // When stats are present the name + divider + grid stack from y=221.
            // Without stats the bottom of the card is empty, so nudge the name
            // a bit lower (still inside the card's natural name slot, not all
            // the way to the bottom edge).
            //
            // Horizontal: card is 254 wide, block is 142 wide, so left =
            // (254 − 142) / 2 = 56 to centre it. (The previous value 48
            // left an 8 px asymmetric gap — barely visible on desktop, more
            // obvious on the mobile carousel where the card sits centered
            // in its slot.)
            val noStats = stats.size < 6
            left = s(56); top = if (noStats) s(245) else s(221); blockWidth = s(142)
            nameFS = 22; nameLH = 26; gap = s(8)
            flagW = 0; flagH = 0
        }

        PlayerCardSize.Medium, PlayerCardSize.Small -> {
            left = s(26); top = s(122); blockWidth = s(78)
            nameFS = 12; nameLH = 12; gap = s(4)
            flagW = 15; flagH = 10
        }

        PlayerCardSize.Arena -> {
            left = s(19); top = s(86); blockWidth = s(56)
            nameFS = 8; nameLH = 14; gap = s(4)
            flagW = 15; flagH = 10
        }

        PlayerCardSize.XtraSmall -> {
            // Name nudged down so the player's face has more breathing room
            // above and the label sits along the card's bottom edge.
            left = s(12); top = s(71); blockWidth = s(35)
            nameFS = 6; nameLH = 5; gap = s(3)
            flagW = 7; flagH = 5
        }

        else -> return
    }

    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.styleModifier {
            property("position", "absolute")
            property("left", left)
            property("top", top)
            property("width", blockWidth)
            property("gap", gap)
            // Own compositing layer (like the avatar) so the name is rasterised
            // up-front and isn't repainted when a page slides into view.
            property("will-change", "transform")
        }
    ) {
        SpanText(
            playerName,
            modifier = Modifier
                .fontFamily(
                    AppTypography.CARD_HEADLINE_FONT,
                    *AppTypography.CARD_HEADLINE_FALLBACKS
                )
                .fontWeight(AppTypography.Bold)
                .sFontSize(nameFS)
                // Repaint the name in the template's `cardTextColor` (e.g.
                // tennis green, padel teal) — callers fold it into `sc` via
                // `effectiveSc`, so `sc.primary` is the right source. Falls
                // back to the brand blue automatically when no template tint
                // is provided (sc stays at the sport default).
                .sLineHeight(nameLH)
                .color(sc.primary)
                .cardTextOutline()
                .textAlign(TextAlign.Center)
                .fillMaxWidth()
                .styleModifier {
                    property("text-transform", "uppercase")
                    property(
                        "letter-spacing",
                        if (size == PlayerCardSize.Large) s(0.22) else s(0.012)
                    )
                }
        )

        // Divider sits between the name and the stat grid. When there are no
        // stats to render (e.g. profile front view with `stats = emptyList()`),
        // the divider would dangle under the name, so suppress both.
        if (size == PlayerCardSize.Large && !isOther && stats.size >= 6) {
            NameDivider(sc)
            StatsGrid(sc, stats)
        }

        if (flagUrl != null && flagW > 0) {
            Image(
                src = flagUrl,
                modifier = Modifier.sWidth(flagW).sHeight(flagH)
                    .styleModifier { property("object-fit", "cover") },
                alt = "flag"
            )
        }
    }
}

// =============================================================================
// Name divider line (gradient, Large card only)
// =============================================================================

@Composable
private fun NameDivider(sc: SportColorScheme) {
    val rgb = sc.primary.toRgb()
    Box(
        Modifier
            .sWidth(129)
            .height(1.px)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(90deg, rgba(${rgb.red},${rgb.green},${rgb.blue},0), rgba(${rgb.red},${rgb.green},${rgb.blue},0.5), rgba(${rgb.red},${rgb.green},${rgb.blue},0))"
                )
            }
    )
}

// =============================================================================
// Sport-colored gradient divider (identity column)
// =============================================================================

@Composable
private fun SportDivider(sc: SportColorScheme) {
    val rgb = sc.primary.toRgb()
    Box(
        Modifier
            .fillMaxWidth()
            .height(1.px)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(90deg, rgba(${rgb.red},${rgb.green},${rgb.blue},0), rgba(${rgb.red},${rgb.green},${rgb.blue},0.6), rgba(${rgb.red},${rgb.green},${rgb.blue},0))"
                )
            }
    )
}

// =============================================================================
// Stats grid (Large card only — 6 stats in 2×3 columns)
// =============================================================================

@Composable
private fun StatsGrid(sc: SportColorScheme, stats: List<PlayerStat>) {
    val rgb = sc.primary.toRgb()
    Row(Modifier.styleModifier { property("gap", s(10)) }) {
        Column { stats.take(3).forEach { StatRow(sc, it) } }
        Box(
            Modifier.width(1.px).sHeight(59).styleModifier {
                property(
                    "background",
                    "linear-gradient(180deg, rgba(${rgb.red},${rgb.green},${rgb.blue},0), rgba(${rgb.red},${rgb.green},${rgb.blue},0.5), rgba(${rgb.red},${rgb.green},${rgb.blue},0))"
                )
            }
        )
        Column { stats.drop(3).take(3).forEach { StatRow(sc, it) } }
    }
}

@Composable
private fun StatRow(sc: SportColorScheme, stat: PlayerStat) {
    Row(Modifier.styleModifier { property("gap", s(8)) }) {
        SpanText(
            "${stat.value}",
            modifier = Modifier
                .fontFamily(AppTypography.CARD_TEXT_FONT, AppTypography.CARD_TEXT_FALLBACK)
                .fontWeight(AppTypography.SemiBold)
                .sFontSize(14)
                .color(sc.primary)
                .sMinWidth(21)
                .textAlign(TextAlign.Center)
                .styleModifier { property("text-transform", "uppercase") }
        )
        SpanText(
            stat.label,
            modifier = Modifier
                .fontFamily(AppTypography.CARD_TEXT_FONT, AppTypography.CARD_TEXT_FALLBACK)
                .fontWeight(AppTypography.SemiBold)
                .sFontSize(14)
                .color(sc.primary)
                .styleModifier { property("text-transform", "uppercase") }
        )
    }
}


